import { useCallback, useSyncExternalStore } from "react";
import { currentUserSubscription } from "@/lib/firestore/subscriptions/current-user";
import { NO_UID_KEY } from "@/lib/firestore/subscription-keys";
import {
  USER_SNAPSHOT_SERVER,
  USER_SNAPSHOT_SIGNED_OUT,
} from "@/lib/firestore/subscription-snapshots";
import { isPremiumActive, type Plan, type User } from "@amazing-wedding/shared-types";
import { useEffectiveUid } from "./useAuth";

interface UseCurrentUserResult {
  user: User | null;
  /** Raw plan stored on /users/{uid}. */
  plan: Plan;
  /** Plan used for feature gating — `"free"` when Premium has expired. */
  effectivePlan: Plan;
  isPremium: boolean;
  loading: boolean;
}

/**
 * Live subscription to the signed-in user's `/users/{uid}` doc. Returns the
 * effective `plan` (defaulting to `"free"`) so callers can gate UI without
 * juggling null checks. Mirrors `useWedding` — real-time `onSnapshot` so a
 * plan flip from the billing webhook updates the UI without a reload.
 *
 * Backed by a refcounted shared Firestore subscription keyed by uid.
 */
export function useCurrentUser(): UseCurrentUserResult {
  const uid = useEffectiveUid() ?? undefined;
  const key = uid ?? NO_UID_KEY;

  const subscribe = useCallback(
    (listener: () => void) => {
      if (!uid) return () => {};
      return currentUserSubscription.subscribe(key, listener);
    },
    [uid, key],
  );

  const getSnapshot = useCallback(() => {
    if (!uid) return USER_SNAPSHOT_SIGNED_OUT;
    return currentUserSubscription.getSnapshot(key);
  }, [uid, key]);

  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => USER_SNAPSHOT_SERVER);

  const user = snapshot.data;
  const plan: Plan = user?.plan ?? "free";
  const isPremium = user
    ? isPremiumActive({
        plan: user.plan,
        planValidUntil: user.planValidUntil,
        mollieSubscriptionId: user.mollieSubscriptionId,
      })
    : false;
  const effectivePlan: Plan = isPremium ? "premium" : "free";

  if (!uid) {
    return { user: null, plan: "free", effectivePlan: "free", isPremium: false, loading: false };
  }

  return { user, plan, effectivePlan, isPremium, loading: snapshot.loading };
}
